import type { SophiWidgetLauncherConfig, SophiWidgetPublicApi } from "./types";

const DEFAULT_CONFIG: Required<Pick<SophiWidgetLauncherConfig, "environment">> & {
  sdk: Required<NonNullable<SophiWidgetLauncherConfig["sdk"]>>;
  launcher: Required<NonNullable<SophiWidgetLauncherConfig["launcher"]>>;
  modal: Required<NonNullable<SophiWidgetLauncherConfig["modal"]>>;
  widget: Required<NonNullable<SophiWidgetLauncherConfig["widget"]>>;
} = {
  environment: "production",
  sdk: {
    autoLoad: false,
    url: "",
  },
  launcher: {
    enabled: true,
    position: "right-center",
    text: "Sohbete Başla",
    backgroundColor: "#111827",
    textColor: "#ffffff",
  },
  modal: {
    title: "Sophi",
  },
  widget: {
    containerSelector: "#sophi-widget",
    width: "100%",
    height: "100%",
  },
};

type SdkInstance = {
  init: (config: Record<string, unknown>) => Promise<void> | void;
  destroy?: () => Promise<void> | void;
  on?: (...args: unknown[]) => unknown;
  off?: (...args: unknown[]) => unknown;
};

const state: {
  sdkInstance: SdkInstance | null;
  initialized: boolean;
  initializing: boolean;
  modalOpen: boolean;
  sdkLoadPromise: Promise<void> | null;
} = {
  sdkInstance: null,
  initialized: false,
  initializing: false,
  modalOpen: false,
  sdkLoadPromise: null,
};

function getConfig(): SophiWidgetLauncherConfig {
  const userConfig = (window.sophiWidgetConfig || {}) as SophiWidgetLauncherConfig;

  return {
    ...DEFAULT_CONFIG,
    ...userConfig,
    sdk: {
      ...DEFAULT_CONFIG.sdk,
      ...(userConfig.sdk || {}),
    },
    launcher: {
      ...DEFAULT_CONFIG.launcher,
      ...(userConfig.launcher || {}),
    },
    modal: {
      ...DEFAULT_CONFIG.modal,
      ...(userConfig.modal || {}),
    },
    widget: {
      ...DEFAULT_CONFIG.widget,
      ...(userConfig.widget || {}),
    },
    handlers: {
      ...(userConfig.handlers || {}),
    },
  };
}

function createStyles(): void {
  if (document.getElementById("sophi-floating-styles")) return;

  const config = getConfig();

  const style = document.createElement("style");
  style.id = "sophi-floating-styles";

  style.innerHTML = `
    #sophi-floating-button {
      position: fixed;
      z-index: 2147483646;
      display: flex;
      align-items: center;
      gap: 8px;
      height: 52px;
      min-width: 52px;
      padding: 0 14px;
      border: none;
      background: ${config.launcher?.backgroundColor};
      color: ${config.launcher?.textColor};
      font-family: Arial, sans-serif;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.24);
      overflow: hidden;
      transition: all 0.2s ease;
    }

    #sophi-floating-button.sophi-position-right-center {
      right: 0;
      top: 50%;
      transform: translateY(-50%);
      border-radius: 26px 0 0 26px;
    }

    #sophi-floating-button.sophi-position-left-center {
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      border-radius: 0 26px 26px 0;
    }

    #sophi-floating-button.sophi-position-right-bottom {
      right: 24px;
      bottom: 24px;
      border-radius: 999px;
    }

    #sophi-floating-button.sophi-position-left-bottom {
      left: 24px;
      bottom: 24px;
      border-radius: 999px;
    }

    #sophi-floating-button .sophi-floating-label {
      max-width: 0;
      opacity: 0;
      white-space: nowrap;
      overflow: hidden;
      transition: all 0.2s ease;
    }

    #sophi-floating-button:hover .sophi-floating-label {
      max-width: 140px;
      opacity: 1;
    }

    #sophi-floating-button .sophi-floating-icon {
      width: 24px;
      height: 24px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    #sophi-modal-overlay {
      position: fixed;
      inset: 0;
      z-index: 2147483647;
      display: none;
      background: rgba(17, 24, 39, 0.72);
    }

    #sophi-modal-overlay.sophi-modal-open {
      display: block;
    }

    #sophi-modal {
      position: fixed;
      inset: 0;
      width: 100%;
      height: 100%;
      background: #ffffff;
      display: flex;
      flex-direction: column;
    }

    #sophi-modal-header {
      height: 56px;
      min-height: 56px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0 16px;
      border-bottom: 1px solid #e5e7eb;
      font-family: Arial, sans-serif;
      box-sizing: border-box;
    }

    #sophi-modal-title {
      font-size: 16px;
      font-weight: 700;
      color: #111827;
    }

    #sophi-modal-close {
      width: 36px;
      height: 36px;
      border: none;
      border-radius: 999px;
      background: #f3f4f6;
      color: #111827;
      font-size: 24px;
      line-height: 1;
      cursor: pointer;
    }

    #sophi-modal-close:hover {
      background: #e5e7eb;
    }

    #sophi-widget {
      flex: 1;
      width: 100%;
      height: calc(100vh - 56px);
      overflow: hidden;
    }

    body.sophi-modal-lock {
      overflow: hidden;
    }
  `;

  document.head.appendChild(style);
}

function createFloatingButton(): void {
  const config = getConfig();

  if (config.launcher?.enabled === false) return;
  if (document.getElementById("sophi-floating-button")) return;

  const button = document.createElement("button");
  button.id = "sophi-floating-button";
  button.type = "button";
  button.className = `sophi-position-${config.launcher?.position || "right-center"}`;
  button.setAttribute("aria-label", config.launcher?.text || "Sohbete Başla");

  button.innerHTML = `
    <span class="sophi-floating-icon" aria-hidden="true">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M5.52616 8.67324C5.16308 8.80738 4.92308 9.152 4.92308 9.53846C4.92308 9.92492 5.16308 10.2695 5.52616 10.4037L7.24184 11.0388C9.57046 11.9015 10.4603 12.2511 11.1052 12.8948C11.7489 13.5397 12.0985 14.4295 12.9612 16.7582L13.5963 18.4738C13.7305 18.8369 14.0751 19.0769 14.4615 19.0769C14.848 19.0769 15.1926 18.8369 15.3268 18.4738L15.9618 16.7582C16.8246 14.4295 17.1742 13.5397 17.8191 12.8948C18.4628 12.2511 19.3526 11.9015 21.6812 11.0388L23.397 10.4037C23.76 10.2695 24 9.92492 24 9.53846C24 9.152 23.76 8.80738 23.397 8.67324L21.6812 8.03816C19.3526 7.17662 18.4628 6.82584 17.8191 6.18092C17.1742 5.53724 16.8246 4.64738 15.9618 2.31876L15.3268 0.603077C15.1926 0.239999 14.848 1.6892e-08 14.4615 0C14.0751 -1.6892e-08 13.7305 0.239999 13.5963 0.603077L12.9612 2.31876C12.0985 4.64738 11.7489 5.53724 11.1052 6.18092C10.4603 6.82584 9.57046 7.17662 7.24184 8.03816L5.52616 8.67324ZM0.603076 18.5194C0.24 18.6535 0 18.9982 0 19.3846C0 19.7711 0.24 20.1156 0.603076 20.2498L1.33785 20.5218C2.38154 20.9084 2.6277 21.0216 2.8037 21.1964C2.9797 21.3724 3.0917 21.6184 3.47816 22.6622L3.75016 23.397C3.8843 23.76 4.22892 24 4.61538 24C5.00184 24 5.34646 23.76 5.48062 23.397L5.75262 22.6622C6.13908 21.6184 6.25108 21.3724 6.42708 21.1964C6.60308 21.0216 6.84924 20.9084 7.89292 20.5218L8.6277 20.2498C8.99076 20.1156 9.23076 19.7711 9.23076 19.3846C9.23076 18.9982 8.99076 18.6535 8.6277 18.5194L7.89292 18.2474C6.84924 17.8609 6.60308 17.7477 6.42708 17.5729C6.25108 17.3969 6.13908 17.1508 5.75262 16.1071L5.48062 15.3723C5.34646 15.0092 5.00184 14.7692 4.61538 14.7692C4.22892 14.7692 3.8843 15.0092 3.75016 15.3723L3.47816 16.1071C3.0917 17.1508 2.9797 17.3969 2.8037 17.5729C2.6277 17.7477 2.38154 17.8609 1.33785 18.2474L0.603076 18.5194Z" fill="white"/>
        </svg>
    </span>
    <span class="sophi-floating-label">${config.launcher?.text || "Sohbete Başla"}</span>
  `;

  button.addEventListener("click", () => {
    void openModal();
  });

  document.body.appendChild(button);
}

function createModal(): void {
  if (document.getElementById("sophi-modal-overlay")) return;

  const config = getConfig();

  const overlay = document.createElement("div");
  overlay.id = "sophi-modal-overlay";

  overlay.innerHTML = `
    <div id="sophi-modal" role="dialog" aria-modal="true" aria-labelledby="sophi-modal-title">
      <div id="sophi-modal-header">
        <div id="sophi-modal-title">${config.modal?.title || "Sophi"}</div>
        <button id="sophi-modal-close" type="button" aria-label="Kapat">×</button>
      </div>
      <div id="sophi-widget"></div>
    </div>
  `;

  document.body.appendChild(overlay);

  document.getElementById("sophi-modal-close")?.addEventListener("click", closeModal);

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && state.modalOpen) {
      closeModal();
    }
  });
}

async function loadSdkIfNeeded(): Promise<void> {
  const config = getConfig();

  if (window.SophiWebSDK?.SophiWidget) return;

  if (!config.sdk?.autoLoad) {
    throw new Error("Sophi SDK is not loaded. Set sdk.autoLoad=true with sdk.url or load the SDK before widget launcher.");
  }

  if (!config.sdk.url) {
    throw new Error("sdk.url is required when sdk.autoLoad=true.");
  }

  if (state.sdkLoadPromise) {
    return state.sdkLoadPromise;
  }

  state.sdkLoadPromise = new Promise<void>((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(`script[src="${config.sdk?.url}"]`);

    if (existingScript) {
      existingScript.addEventListener("load", () => resolve());
      existingScript.addEventListener("error", () => reject(new Error("Failed to load Sophi SDK.")));
      return;
    }

    const script = document.createElement("script");
    script.src = config.sdk?.url || "";
    script.async = true;

    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Sophi SDK."));

    document.head.appendChild(script);
  });

  return state.sdkLoadPromise;
}

async function openModal(): Promise<unknown> {
  createModal();

  const overlay = document.getElementById("sophi-modal-overlay");
  overlay?.classList.add("sophi-modal-open");

  document.body.classList.add("sophi-modal-lock");

  state.modalOpen = true;

  return initWidget();
}

function closeModal(): void {
  const overlay = document.getElementById("sophi-modal-overlay");

  overlay?.classList.remove("sophi-modal-open");
  document.body.classList.remove("sophi-modal-lock");

  state.modalOpen = false;
}

async function initWidget(): Promise<unknown> {
  if (state.initialized || state.initializing) {
    return state.sdkInstance;
  }

  state.initializing = true;

  try {
    await loadSdkIfNeeded();

    if (!window.SophiWebSDK?.SophiWidget) {
      throw new Error("window.SophiWebSDK.SophiWidget not found.");
    }

    const config = getConfig();

    if (!config.apiKey) {
      throw new Error("window.sophiWidgetConfig.apiKey is required.");
    }

    const sdkInstance = new window.SophiWebSDK.SophiWidget();

    state.sdkInstance = sdkInstance;

    exposePublicApi();

    await sdkInstance.init({
      apiKey: config.apiKey,
      container: config.widget?.containerSelector || "#sophi-widget",
      userId: config.userId,
      userName: config.userName,
      environment: config.environment || "production",
      width: config.widget?.width || "100%",
      height: config.widget?.height || "100%",
      onReady: () => {
        state.initialized = true;
        state.initializing = false;
        console.log("[Sophi] Widget is ready.");
      },
      onError: (error: unknown) => {
        state.initializing = false;
        console.error("[Sophi] Init error:", error);
      },
    });

    bindWidgetEvents(sdkInstance);

    return sdkInstance;
  } catch (error) {
    state.initializing = false;
    console.error("[Sophi] Widget init failed:", error);
    return null;
  }
}

function bindWidgetEvents(sdkInstance: SdkInstance): void {
  if (!sdkInstance.on) return;

  const config = getConfig();

  sdkInstance.on("add_to_cart", (data: any) => {
    if (config.handlers?.addToCart) {
      return config.handlers.addToCart(data);
    }

    if (window.sophiAddToCart) {
      return window.sophiAddToCart(data);
    }

    console.warn("[Sophi] addToCart handler is not defined.", data);
    return undefined;
  });

  sdkInstance.on("send_to_checkout", (data: any) => {
    if (config.handlers?.sendToCheckout) {
      return config.handlers.sendToCheckout(data);
    }

    if (window.sophiSendToCheckout) {
      return window.sophiSendToCheckout(data);
    }

    console.warn("[Sophi] sendToCheckout handler is not defined.", data);
    return undefined;
  });

  sdkInstance.on("add_to_favorite", (data: any) => {
    if (config.handlers?.addToFavorite) {
      return config.handlers.addToFavorite(data);
    }

    if (window.sophiAddToFavorite) {
      return window.sophiAddToFavorite(data);
    }

    return undefined;
  });

  sdkInstance.on("error", (error: any) => {
    if (config.handlers?.error) {
      return config.handlers.error(error);
    }

    console.error("[Sophi] Widget error:", error);
    return undefined;
  });
}

async function destroyWidget(): Promise<void> {
  try {
    if (state.sdkInstance?.destroy) {
      await state.sdkInstance.destroy();
    }
  } catch (error) {
    console.error("[Sophi] Destroy error:", error);
  } finally {
    state.sdkInstance = null;
    state.initialized = false;
    state.initializing = false;

    exposePublicApi();
  }
}

function exposePublicApi(): void {
  const publicApi: SophiWidgetPublicApi = {
    open: openModal,
    close: closeModal,
    init: initWidget,
    destroy: destroyWidget,
    getInstance: () => state.sdkInstance,
    isInitialized: () => state.initialized,
    isOpen: () => state.modalOpen,
    setUserId,
    setUser,
  };

  publicApi.on = (...args: unknown[]) => {
    const instance = state.sdkInstance;

    if (!instance?.on) {
      console.warn("[Sophi] Widget instance is not ready yet.");
      return undefined;
    }

    return instance.on(...args);
  };

  publicApi.off = (...args: unknown[]) => {
    const instance = state.sdkInstance;

    if (!instance?.off) {
      console.warn("[Sophi] Widget instance is not ready yet.");
      return undefined;
    }

    return instance.off(...args);
  };

  window.sophiWidget = publicApi;
}

function boot(): void {
  exposePublicApi();
  createStyles();
  createFloatingButton();
}

export function initSophiWidgetLauncher(config?: SophiWidgetLauncherConfig): SophiWidgetPublicApi {
  if (config) {
    window.sophiWidgetConfig = {
      ...(window.sophiWidgetConfig || {}),
      ...config,
    };
  }

  boot();

  return window.sophiWidget as SophiWidgetPublicApi;
}
async function setUserId(userId?: string | null): Promise<unknown> {
  return setUser(userId);
}

async function setUser(userId?: string | null, userName?: string | null): Promise<unknown> {
  const normalizedUserId = userId || undefined;
  const normalizedUserName = userName || undefined;

  window.sophiWidgetConfig = {
    ...(window.sophiWidgetConfig || {}),
    userId: normalizedUserId,
    userName: normalizedUserName,
  };

  /**
   * Widget hiç init edilmediyse sadece config'i güncelle.
   * İlk open/init çağrısında yeni userId/userName ile başlayacak.
   */
  if (!state.sdkInstance && !state.initialized && !state.initializing) {
    return null;
  }

  /**
   * Init devam ediyorsa önce mevcut init'in bitmesini beklemek yerine
   * güvenli tarafta kalıp destroy + reinit akışına sokuyoruz.
   */
  await destroyWidget();

  /**
   * Modal açıksa kullanıcı zaten widget ekranında demektir.
   * Yeni userId/userName ile aynı container içinde tekrar init ediyoruz.
   */
  if (state.modalOpen) {
    return initWidget();
  }

  /**
   * Modal kapalıysa reinit etmiyoruz.
   * Bir sonraki open/init yeni userId/userName ile çalışacak.
   */
  return null;
}

if (typeof window !== "undefined") {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
}
